import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import ConfirmModal from "../../components/common/ConfirmModal";
import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import StatusChip from "../../components/common/StatusChip";

import { USER_LIST_STRINGS, COMMON_STRINGS } from "../../constants/strings";

import { getUsers, updateUserStatus } from "../../services/api";

// 🔥 Redux
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

import "../table-common.css";

const UserListPage = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // ================= FETCH =================
  const fetchUsers = async () => {
    try {
      setLoading(true);

      const res = await getUsers({ limit: 1000 });

      // 🔥 normalize status (IMPORTANT FIX)
      const formatted = (res.data.data || []).map((user: any) => ({
        ...user,
        status: user.status?.toLowerCase(),
      }));

      setRows(formatted);
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ================= UPDATE =================
  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      // 🔥 optimistic update
      setRows((prev) =>
        prev.map((row) =>
          row._id === id ? { ...row, status: newStatus } : row,
        ),
      );

      await updateUserStatus(id, newStatus);

      dispatch(
        showSnackbar({
          message: "User status updated successfully",
          type: "success",
        }),
      );
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );

      fetchUsers(); // rollback
    }
  };

  const handleConfirmBlock = () => {
    if (selectedId) {
      handleStatusChange(selectedId, "blocked");
    }
    setConfirmOpen(false);
  };

  // ================= FILTER =================
  const filteredRows = rows.filter((row) => {
    const matchSearch = Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(searchText.toLowerCase());

    const matchStatus = statusFilter ? row.status === statusFilter : true;

    return matchSearch && matchStatus;
  });

  // ================= COLUMNS =================
  const columns: GridColDef[] = [
    {
      field: "thumbnail",
      headerName: "Thumbnail",
      width: 90,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {
        const user = params.row;

        // ✅ Proper image URL fix
        let imageUrl = user.profileImage;

        if (imageUrl && !imageUrl.startsWith("http")) {
          imageUrl = `https://api.johukumservices.com/uploads/profile/${imageUrl}`;
        }

        const hasImage = !!imageUrl;

        const firstChar =
          user.name?.charAt(0)?.toUpperCase() || user.mobile?.charAt(0) || "?";

        // 🎨 dynamic color
        const colors = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2"];
        const bgColor = colors[(firstChar.charCodeAt(0) || 0) % colors.length];

        return (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: hasImage ? "transparent" : bgColor,
              color: "#fff",
              fontWeight: 600,
              fontSize: "16px",
              overflow: "hidden",
            }}
          >
            {hasImage ? (
              <Box
                component="img"
                src={imageUrl}
                alt={user.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              firstChar
            )}
          </Box>
        );
      },
    },
    // 👤 NAME
    {
      field: "name",
      headerName: USER_LIST_STRINGS.title,
      flex: 1,
      renderCell: (params) => params.row.name || "N/A",
    },

    // 📱 MOBILE
    {
      field: "mobile",
      headerName: "Phone",
      flex: 1,
    },

    // 🏠 ADDRESS (🔥 NEW)
    {
      field: "address",
      headerName: "Address",
      flex: 2,
      renderCell: (params) => {
        const user = params.row;

        return (
          [user.houseNo, user.city, user.state, user.pincode]
            .filter(Boolean)
            .join(", ") || "N/A"
        );
      },
    },

    // 📊 STATUS
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => <StatusChip status={params.value} />,
    },

    // 🔥 ACTIONS (same)
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      sortable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {
        const currentStatus = params.row.status?.toLowerCase();

        return (
          <Box className="action-cell">
            <Stack direction="row" spacing={0.5}>
              {/* ❌ BLOCK */}
              <Tooltip
                title={
                  currentStatus === "blocked"
                    ? "User already blocked"
                    : "Block User"
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={currentStatus === "blocked"}
                    onClick={() => {
                      setSelectedId(params.row._id);
                      setConfirmOpen(true);
                    }}
                    sx={{
                      minWidth: "32px",
                      padding: "6px",
                      color: "var(--danger)",
                      borderColor: "var(--danger)",
                    }}
                  >
                    <BlockIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>

              {/* ✅ UNBLOCK */}
              <Tooltip
                title={
                  currentStatus === "active"
                    ? "User already active"
                    : "Unblock User"
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={currentStatus === "active"}
                    onClick={() => handleStatusChange(params.row._id, "active")}
                    sx={{
                      minWidth: "32px",
                      padding: "6px",
                      backgroundColor: "var(--success)",
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      {/* 🔥 HEADER */}
      <PageHeader
        title={USER_LIST_STRINGS.title}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={USER_LIST_STRINGS.searchPlaceholder}
        filterValue={statusFilter}
        onFilterChange={setStatusFilter}
        filterOptions={[
          { label: "All", value: "" },
          { label: USER_LIST_STRINGS.active, value: "active" },
          { label: USER_LIST_STRINGS.blocked, value: "blocked" },
        ]}
      />

      {/* 📊 TABLE */}
      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
      />

      {/* 🔥 CONFIRM MODAL */}
      <ConfirmModal
        open={confirmOpen}
        title="Block User"
        description="Are you sure you want to block this user?"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmBlock}
      />
    </Box>
  );
};

export default UserListPage;
